import React from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import "./BoardsDetailsSustainability.css";
import { resolveMediaUrl } from "../../../../../utils/media";
import { formatUnitSuperscriptText } from "../../../../../utils/unitSuperscriptText";

import sus1 from "../../../../../assets/images/allproducts/boards/sus1.svg";
import sus2 from "../../../../../assets/images/allproducts/boards/sus2.svg";
import sus3 from "../../../../../assets/images/allproducts/boards/sus3.svg";
import sus4 from "../../../../../assets/images/allproducts/boards/sus4.svg";

const SUSTAINABILITY_CARDS = [
  {
    icon: sus1,
    title: "Recyclability",
    text: "Gypsum board is recyclable as gypsum-based construction waste when separated from non-gypsum contaminants.",
  },
  {
    icon: sus2,
    title: "SVHC",
    text: "No Substances of Very High Concern (SVHC) are intentionally added per manufacturer declaration.",
  },
  {
    icon: sus3,
    title: "VOC Content",
    text: "Volatile Organic Compound Content is declared as very low (<0.05 mg/m3) for regular gypsum boards according to CDPH Standard Method v1.2 (CA 01350).",
  },
  {
    icon: sus4,
    title: "Asbestos Content",
    text: "Product is asbestos-free.",
  },
];

const STORAGE_ITEMS = [
  "Store indoors in a dry, well-ventilated facility protected from weather and temperatures exceeding 52°C, per manufacturer guidance and GA-216.",
  "Stack flat on a firm, level platform with continuous support; do not exceed 5 m stack height.",
  "Keep pallets above ground level and out of direct contact with water, grease, or oil.",
  "Cover stacks with plastic sheeting during site staging, while allowing ventilation to avoid condensation.",
  "Keep pallets out of traffic aisles; use corner protectors if exposure to impact is unavoidable.",
  "Avoid direct rain or prolonged sunlight exposure and prevent impact damage during handling and installation.",
];

const BoardsDetailsSustainability = ({ board }) => {
  const { t } = useTranslation();
  const resolveCardIcon = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("/uploads/")) {
      return resolveMediaUrl(raw) || raw;
    }
    return raw;
  };

  const cards =
    Array.isArray(board?.sustainabilityCards) && board.sustainabilityCards.length
      ? board.sustainabilityCards.map((card) => ({
          icon: resolveCardIcon(card?.iconUrl),
          title: card?.title || "",
          text: card?.text || "",
        }))
      : SUSTAINABILITY_CARDS;

  const storageItems =
    Array.isArray(board?.sustainabilityStorageItems) && board.sustainabilityStorageItems.length
      ? board.sustainabilityStorageItems.map((item) => item?.text || "").filter(Boolean)
      : STORAGE_ITEMS;

  return (
    <section className="boardsSus" aria-label="Sustainability">
      <div className="boardsSus-inner">
        <h2 className="boardsSus-mainTitle">{t("sections.products.boardTabs.sustainability")}</h2>
        <div className="boardsSus-titleRule" aria-hidden="true" />

        <div className="boardsSus-cards">
          {cards.map((card) => (
            <div className="boardsSus-card" key={card.title}>
              <div className="boardsSus-iconWrap">
                <img src={card.icon} alt="" className="boardsSus-icon" draggable={false} />
              </div>
              <div>
                <h3 className="boardsSus-cardTitle">{formatUnitSuperscriptText(card.title)}</h3>
                <p className="boardsSus-cardText">{formatUnitSuperscriptText(card.text)}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="boardsSus-storageTitle">
          <span className="boardsSus-storageTitleStrong">
            {t("sections.products.boardTabs.storageTitleStrong")}
          </span>{" "}
          <span className="boardsSus-storageTitleSoft">
            {t("sections.products.boardTabs.storageTitleSoft")}
          </span>
        </h2>

        <ul className="boardsSus-chevronList">
          {storageItems.map((line, i) => (
            <li key={i} className="boardsSus-chevronItem">
              <FiChevronRight className="boardsSus-chevronIcon" aria-hidden />
              <span>{formatUnitSuperscriptText(line)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default BoardsDetailsSustainability;
